/**
 * Typed API client for all Flax endpoints.
 *
 * All requests go through /api/* which the Vite dev proxy (or nginx in prod)
 * forwards to the Express API on port 3000.
 *
 * JWT token is stored in memory (not localStorage) and sent via Authorization header.
 * The token is refreshed on 401 responses.
 */

const BASE = "/api";

let token: string | null = null;
let refreshTokenValue: string | null = null;

export function setToken(jwt: string | null) {
  token = jwt;
}

export function getToken(): string | null {
  return token;
}

export function setRefreshToken(rt: string | null) {
  refreshTokenValue = rt;
}

export function getRefreshToken(): string | null {
  return refreshTokenValue;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && refreshTokenValue) {
    // Attempt silent refresh
    try {
      const refreshRes = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: refreshTokenValue }),
      });
      if (refreshRes.ok) {
        const data = await refreshRes.json();
        token = data.token;
        refreshTokenValue = data.refreshToken;
        // Retry the original request with the new token
        const retryRes = await fetch(`${BASE}${path}`, {
          method,
          headers: { ...headers, Authorization: `Bearer ${token}` },
          body: body ? JSON.stringify(body) : undefined,
        });
        if (retryRes.ok) return retryRes.json();
      }
    } catch {
      // Refresh failed — fall through to logout
    }
    token = null;
    refreshTokenValue = null;
    throw new ApiError(401, "Session expired — please log in again");
  }

  if (res.status === 401) {
    token = null;
    refreshTokenValue = null;
    throw new ApiError(401, "Unauthorized — please log in again");
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new ApiError(res.status, data.message ?? data.error ?? res.statusText, data);
  }

  return res.json();
}

// ── Typed Endpoints ─────────────────────────────────────────────────────────

// Health
export const health = () => request<{ status: string }>("GET", "/health");

// Auth
export const login = (email: string, password: string) =>
  request<{ token: string; refreshToken: string; user: UserSummary }>("POST", "/auth/login", { email, password });

export const register = (email: string, password: string, displayName: string) =>
  request<{ token: string; refreshToken: string; user: UserSummary }>("POST", "/auth/register", { email, password, displayName });

export const refreshAuth = () =>
  request<{ token: string; refreshToken: string }>("POST", "/auth/refresh", { refreshToken: refreshTokenValue });

export interface OAuthProvider {
  id: string;
  name: string;
}

export const getOAuthProviders = () =>
  request<{ providers: OAuthProvider[] }>("GET", "/auth/oauth/providers");

// Agents
export const listAgents = () => request<{ agents: Agent[] }>("GET", "/agents");
export const createAgent = (data: CreateAgentInput) =>
  request<Agent>("POST", "/agents", data);
export const getAgent = (id: string) => request<Agent>("GET", `/agents/${id}`);

// Agent Update
export interface UpdateAgentInput {
  name?: string;
  description?: string;
  systemPrompt?: string;
  model?: string;
  provider?: string | null;
  apiBaseUrl?: string | null;
  toolPolicyProfile?: string;
  enabled?: boolean;
  tokenBudget?: number | null;
}

export const updateAgent = (id: string, data: UpdateAgentInput) =>
  request<Agent>("PUT", `/agents/${id}`, data);

// Agent-Skill Binding
export interface AgentSkillBinding {
  id: string;
  skillId: string;
  name: string;
  version: string;
  author?: string;
  tools: string[];
  riskLevel: string;
  approved: boolean;
  addedAt: string;
}

export const listAgentSkills = (agentId: string) =>
  request<{ skills: AgentSkillBinding[] }>("GET", `/agents/${agentId}/skills`);
export const replaceAgentSkills = (agentId: string, skillIds: string[]) =>
  request<{ skills: AgentSkillBinding[] }>("PUT", `/agents/${agentId}/skills`, { skillIds });
export const bindAgentSkill = (agentId: string, skillId: string) =>
  request<{ bound: boolean }>("POST", `/agents/${agentId}/skills/${skillId}`);
export const unbindAgentSkill = (agentId: string, skillId: string) =>
  request<{ unbound: boolean }>("DELETE", `/agents/${agentId}/skills/${skillId}`);

// Agent Memory
export interface MemoryEntry {
  key: string;
  value: string;
  userId: string;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export const listMemoryEntries = (agentId: string, userId?: string) =>
  request<{ entries: MemoryEntry[] }>("GET", userId ? `/agents/${agentId}/memory?userId=${encodeURIComponent(userId)}` : `/agents/${agentId}/memory`);
export const deleteMemoryEntry = (agentId: string, userId: string, key: string) =>
  request<{ deleted: boolean }>("DELETE", `/agents/${agentId}/memory/${encodeURIComponent(key)}?userId=${encodeURIComponent(userId)}`);
export const clearMemoryEntries = (agentId: string, userId?: string) =>
  request<{ deleted: boolean }>("DELETE", userId ? `/agents/${agentId}/memory?userId=${encodeURIComponent(userId)}` : `/agents/${agentId}/memory`);

// Conversations
export const listConversations = (search?: string) =>
  request<{ conversations: ConversationSummary[] }>("GET", search ? `/conversations?search=${encodeURIComponent(search)}` : "/conversations");
export const createConversation = (agentId: string) =>
  request<Conversation>("POST", "/conversations", { agentId });
export const sendMessage = (conversationId: string, content: string) =>
  request<{ conversationId: string; message: Message; state: string; usage?: TokenUsage }>(
    "POST",
    `/conversations/${conversationId}/messages`,
    { content },
  );
export const renameConversation = (conversationId: string, title: string) =>
  request<{ id: string; title: string }>("PATCH", `/conversations/${conversationId}`, { title });
export const deleteConversation = (conversationId: string) =>
  request<{ deleted: boolean }>("DELETE", `/conversations/${conversationId}`);
export const getConversationUsage = (conversationId: string) =>
  request<ConversationUsage>("GET", `/conversations/${conversationId}/usage`);

/**
 * Send a message with real-time streaming via SSE.
 * The POST response itself is an SSE stream with token-by-token text delivery.
 */
export function sendMessageStreaming(
  conversationId: string,
  content: string,
  onEvent: (event: StreamEvent) => void,
  onError?: (error: Error) => void,
): AbortController {
  const controller = new AbortController();
  const url = `${BASE}/conversations/${conversationId}/messages?stream=true`;

  fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ content }),
    signal: controller.signal,
  })
    .then(async (res) => {
      if (!res.ok || !res.body) {
        onError?.(new Error(`Streaming failed: ${res.status}`));
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        let eventType = "";
        for (const line of lines) {
          if (line.startsWith("event: ")) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith("data: ") && eventType) {
            try {
              const data = JSON.parse(line.slice(6));
              onEvent({ type: eventType, data } as StreamEvent);
            } catch {
              // Skip unparseable data
            }
            eventType = "";
          }
        }
      }
    })
    .catch((err) => {
      if (err.name !== "AbortError") {
        onError?.(err);
      }
    });

  return controller;
}
export const getMessages = (conversationId: string) =>
  request<{ conversationId: string; messages: Message[] }>(
    "GET",
    `/conversations/${conversationId}/messages`,
  );

// Tools
export const listTools = () => request<{ tools: Tool[] }>("GET", "/tools");

// Workflows
export const listWorkflows = () => request<{ workflows: Workflow[] }>("GET", "/workflows");
export const createWorkflow = (data: CreateWorkflowInput) =>
  request<Workflow>("POST", "/workflows", data);
export const getWorkflow = (id: string) => request<Workflow>("GET", `/workflows/${id}`);
export const runWorkflow = (id: string, input?: Record<string, unknown>, debugMode?: boolean) =>
  request<WorkflowRun>("POST", `/workflows/${id}/run`, { input: input ?? {}, debugMode: debugMode ?? false });
export const getWorkflowRun = (workflowId: string, runId: string) =>
  request<WorkflowRun>("GET", `/workflows/${workflowId}/runs/${runId}`);
export const getWorkflowRunState = (workflowId: string, runId: string) =>
  request<WorkflowRunState>("GET", `/workflows/${workflowId}/runs/${runId}/state`);
export const stepWorkflowRun = (workflowId: string, runId: string) =>
  request<WorkflowRun>("POST", `/workflows/${workflowId}/runs/${runId}/step`);
export const continueWorkflowRun = (workflowId: string, runId: string) =>
  request<WorkflowRun>("POST", `/workflows/${workflowId}/runs/${runId}/continue`);
export const abortWorkflowRun = (workflowId: string, runId: string) =>
  request<WorkflowRun>("POST", `/workflows/${workflowId}/runs/${runId}/abort`);
export const updateWorkflow = (id: string, data: UpdateWorkflowInput) =>
  request<Workflow>("PUT", `/workflows/${id}`, data);
export const deleteWorkflow = (id: string) =>
  request<{ deleted: boolean }>("DELETE", `/workflows/${id}`);

// Skills
export const listSkills = (params?: { tier?: SkillTier; category?: string; search?: string }) => {
  const qs = new URLSearchParams();
  if (params?.tier) qs.set("tier", params.tier);
  if (params?.category) qs.set("category", params.category);
  if (params?.search) qs.set("search", params.search);
  const query = qs.toString();
  return request<{ skills: Skill[] }>("GET", `/skills${query ? `?${query}` : ""}`);
};
export const getSkillDetail = (id: string) =>
  request<Skill & { content: string }>("GET", `/skills/${id}/detail`);
export const createSkill = (content: string, autoApprove = false) =>
  request<Skill>("POST", "/skills", { content, autoApprove });
export const verifySkill = (id: string) =>
  request<{ valid: boolean; error?: string }>("POST", `/skills/${id}/verify`);
export const approveSkill = (id: string) =>
  request<{ approved: boolean }>("POST", `/skills/${id}/approve`);
export const promoteSkill = (id: string) =>
  request<Skill>("POST", `/skills/${id}/promote`);
export const deleteSkill = (id: string) =>
  request<{ deleted: boolean }>("DELETE", `/skills/${id}`);

// Workflow Templates
export interface WorkflowTemplate {
  id: string;
  name: string;
  description?: string;
  category: string;
  definition: { nodes: any[]; edges: any[] };
  tags: string[];
  difficulty: "beginner" | "intermediate" | "advanced";
  author: string;
  featured: boolean;
  installCount: number;
  createdAt: string;
}

export const listWorkflowTemplates = (params?: { category?: string; difficulty?: string; search?: string }) => {
  const qs = new URLSearchParams();
  if (params?.category) qs.set("category", params.category);
  if (params?.difficulty) qs.set("difficulty", params.difficulty);
  if (params?.search) qs.set("search", params.search);
  const query = qs.toString();
  return request<{ templates: WorkflowTemplate[] }>("GET", `/workflow-templates${query ? `?${query}` : ""}`);
};
export const getWorkflowTemplate = (id: string) =>
  request<WorkflowTemplate>("GET", `/workflow-templates/${id}`);
export const installWorkflowTemplate = (id: string, agentId: string, name?: string) =>
  request<{ workflow: Workflow; templateId: string }>("POST", `/workflow-templates/${id}/install`, { agentId, name });

// Workflow Triggers
export type TriggerType = "cron" | "webhook" | "event";

export interface WorkflowTrigger {
  id: string;
  workflowId: string;
  type: TriggerType;
  config: Record<string, unknown>;
  enabled: boolean;
  lastTriggeredAt: string | null;
  createdAt: string;
}

export const listWorkflowTriggers = (workflowId: string) =>
  request<{ triggers: WorkflowTrigger[] }>("GET", `/workflows/${workflowId}/triggers`);
export const createWorkflowTrigger = (workflowId: string, data: { type: TriggerType; config: Record<string, unknown>; enabled?: boolean }) =>
  request<WorkflowTrigger>("POST", `/workflows/${workflowId}/triggers`, data);
export const updateWorkflowTrigger = (workflowId: string, triggerId: string, data: Partial<WorkflowTrigger>) =>
  request<WorkflowTrigger>("PUT", `/workflows/${workflowId}/triggers/${triggerId}`, data);
export const deleteWorkflowTrigger = (workflowId: string, triggerId: string) =>
  request<{ deleted: boolean }>("DELETE", `/workflows/${workflowId}/triggers/${triggerId}`);

// Workflow Compilation
export const compileWorkflow = (workflowId: string) =>
  request<{ compilationId: string; checksum: string; compiledAt: string }>("POST", `/workflows/${workflowId}/compile`);
export const verifyWorkflowCompilation = (workflowId: string) =>
  request<{ valid: boolean; storedChecksum: string; currentChecksum: string; compiledAt: string }>("GET", `/workflows/${workflowId}/verify`);

// Connectors
export type ConnectorLifecycle = "draft" | "published" | "deprecated";
export interface Connector {
  id: string;
  slug: string;
  name: string;
  description?: string;
  version: string;
  lifecycle: ConnectorLifecycle;
  endpoint?: string;
  riskLevel: string;
  operations: Array<{ name: string; description: string; inputSchema: Record<string, unknown>; outputSchema?: Record<string, unknown> }>;
  executionMode: "sandbox" | "native";
  createdAt: string;
}
export interface BuiltinTool {
  id: string;
  name: string;
  description: string;
  riskLevel: string;
  enabled: boolean;
  source: "builtin";
}
export const listConnectors = () =>
  request<{ connectors: Connector[]; builtinTools: BuiltinTool[] }>("GET", "/connectors");
export const createConnector = (data: {
  slug: string;
  name: string;
  description?: string;
  endpoint?: string;
  riskLevel?: string;
  operations?: Connector["operations"];
  executionMode?: "sandbox" | "native";
}) => request<Connector>("POST", "/connectors", data);
export const getConnector = (id: string) =>
  request<Connector>("GET", `/connectors/${id}`);
export const publishConnector = (id: string) =>
  request<Connector>("POST", `/connectors/${id}/publish`);
export const deprecateConnector = (id: string) =>
  request<Connector>("POST", `/connectors/${id}/deprecate`);
export const deleteConnector = (id: string) =>
  request<{ deleted: boolean }>("DELETE", `/connectors/${id}`);

// MCP Servers
export interface MCPServer {
  id: string;
  name: string;
  description?: string;
  url: string;
  allowlist?: string[];
  blocklist?: string[];
  discoveredTools?: MCPTool[];
  createdAt: string;
}
export interface MCPTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
}
export const listMcpServers = () =>
  request<{ servers: MCPServer[] }>("GET", "/mcp-servers");
export const createMcpServer = (data: {
  name: string;
  description?: string;
  url: string;
  allowlist?: string[];
  blocklist?: string[];
}) => request<MCPServer>("POST", "/mcp-servers", data);
export const getMcpServer = (id: string) =>
  request<MCPServer>("GET", `/mcp-servers/${id}`);
export const discoverMcpTools = (id: string) =>
  request<{ tools: MCPTool[]; source: "live" | "cached" }>("GET", `/mcp-servers/${id}/tools`);
export const updateMcpServer = (id: string, data: {
  name?: string;
  description?: string;
  allowlist?: string[];
  blocklist?: string[];
}) => request<MCPServer>("PUT", `/mcp-servers/${id}`, data);
export const deleteMcpServer = (id: string) =>
  request<{ deleted: boolean }>("DELETE", `/mcp-servers/${id}`);

// Agent Templates
export type TemplateCategory = "assistant" | "code" | "security" | "data" | "research" | "custom";

export interface AgentTemplate {
  id: string;
  name: string;
  description?: string;
  category: TemplateCategory;
  systemPrompt: string;
  model: string;
  provider?: string;
  toolPolicyProfile: string;
  skills: string[];
  tools: string[];
  icon?: string;
  author: string;
  version: string;
  tags: string[];
  featured: boolean;
  installCount: number;
  sha256: string;
  createdAt: string;
}

export const listAgentTemplates = (params?: { category?: string; search?: string; featured?: boolean; sort?: string }) => {
  const qs = new URLSearchParams();
  if (params?.category) qs.set("category", params.category);
  if (params?.search) qs.set("search", params.search);
  if (params?.featured !== undefined) qs.set("featured", String(params.featured));
  if (params?.sort) qs.set("sort", params.sort);
  const query = qs.toString();
  return request<{ templates: AgentTemplate[]; categories: TemplateCategory[] }>("GET", `/agent-templates${query ? `?${query}` : ""}`);
};
export const getAgentTemplate = (id: string) =>
  request<AgentTemplate>("GET", `/agent-templates/${id}`);
export const createAgentTemplate = (data: Omit<AgentTemplate, "id" | "author" | "installCount" | "sha256" | "createdAt">) =>
  request<AgentTemplate>("POST", "/agent-templates", data);
export const updateAgentTemplate = (id: string, data: Partial<AgentTemplate>) =>
  request<AgentTemplate>("PUT", `/agent-templates/${id}`, data);
export const deleteAgentTemplate = (id: string) =>
  request<{ deleted: boolean }>("DELETE", `/agent-templates/${id}`);
export const installAgentTemplate = (id: string, overrides?: { name?: string; model?: string; toolPolicyProfile?: string }) =>
  request<{ agent: Agent; templateId: string }>("POST", `/agent-templates/${id}/install`, overrides ?? {});

// Agent Import/Export/Clone
export const exportAgent = (id: string) =>
  request<Record<string, unknown>>("GET", `/agents/${id}/export`);
export const importAgent = (data: { name: string; description?: string; model?: string; systemPrompt: string; toolPolicy?: string; skills?: string[] }) =>
  request<Agent>("POST", "/agents/import", data);
export const cloneAgent = (id: string) =>
  request<Agent>("POST", `/agents/${id}/clone`);

// Planner
export interface PlanStep {
  id: string;
  description: string;
  toolName: string;
  toolVersion: string;
  inputs: Record<string, unknown>;
  dependencies: string[];
  expectedOutputs: string[];
  safetyBudget: { maxTokens?: number; maxDurationMs?: number };
}

export interface AgentPlan {
  id: string;
  goal: string;
  steps: PlanStep[];
  estimatedDurationMs: number;
  toolsUsed: string[];
  status: "draft" | "approved" | "rejected" | "executing" | "completed" | "failed";
  createdAt: string;
}

export const generatePlan = (goal: string, agentId?: string) =>
  request<AgentPlan>("POST", "/planner/plan", { goal, agentId });
export const getPlan = (id: string) =>
  request<AgentPlan>("GET", `/planner/plan/${id}`);
export const approvePlan = (id: string) =>
  request<AgentPlan>("POST", `/planner/plan/${id}/approve`);
export const rejectPlan = (id: string) =>
  request<AgentPlan>("POST", `/planner/plan/${id}/reject`);
export const executePlan = (id: string, agentId: string) =>
  request<{ planId: string; workflowId: string; status: string }>("POST", `/planner/plan/${id}/execute`, { agentId });

// Workflow Run SSE + Approval
export const approveWorkflowNode = (workflowId: string, runId: string, nodeId: string) =>
  request<{ approved: boolean; nodeId: string }>("POST", `/workflows/${workflowId}/runs/${runId}/nodes/${nodeId}/approve`);
export const denyWorkflowNode = (workflowId: string, runId: string, nodeId: string) =>
  request<{ denied: boolean; nodeId: string }>("POST", `/workflows/${workflowId}/runs/${runId}/nodes/${nodeId}/deny`);

// Agent Schedules
export interface AgentSchedule {
  id: string;
  agentId: string;
  name: string;
  cronExpression: string;
  timezone: string;
  prompt: string;
  enabled: boolean;
  tokenBudget?: number | null;
  lastRunAt: string | null;
  nextRunAt: string | null;
  createdAt: string;
}

export const listAgentSchedules = (agentId: string) =>
  request<{ schedules: AgentSchedule[] }>("GET", `/agents/${agentId}/schedules`);
export const createAgentSchedule = (agentId: string, data: {
  name: string; cronExpression: string; timezone?: string; prompt: string; enabled?: boolean; tokenBudget?: number;
}) => request<AgentSchedule>("POST", `/agents/${agentId}/schedules`, data);
export const updateAgentSchedule = (agentId: string, scheduleId: string, data: Partial<AgentSchedule>) =>
  request<AgentSchedule>("PUT", `/agents/${agentId}/schedules/${scheduleId}`, data);
export const deleteAgentSchedule = (agentId: string, scheduleId: string) =>
  request<{ deleted: boolean }>("DELETE", `/agents/${agentId}/schedules/${scheduleId}`);

// Agent Event Triggers
export interface AgentEventTrigger {
  id: string;
  agentId: string;
  eventType: string;
  filter: Record<string, unknown>;
  promptTemplate: string;
  enabled: boolean;
  createdAt: string;
}

export const listAgentEventTriggers = (agentId: string) =>
  request<{ triggers: AgentEventTrigger[] }>("GET", `/agents/${agentId}/event-triggers`);
export const createAgentEventTrigger = (agentId: string, data: {
  eventType: string; filter?: Record<string, unknown>; promptTemplate: string; enabled?: boolean;
}) => request<AgentEventTrigger>("POST", `/agents/${agentId}/event-triggers`, data);
export const updateAgentEventTrigger = (agentId: string, triggerId: string, data: Partial<AgentEventTrigger>) =>
  request<AgentEventTrigger>("PUT", `/agents/${agentId}/event-triggers/${triggerId}`, data);
export const deleteAgentEventTrigger = (agentId: string, triggerId: string) =>
  request<{ deleted: boolean }>("DELETE", `/agents/${agentId}/event-triggers/${triggerId}`);

// Channels
export type ChannelType = "webhook" | "slack" | "telegram" | "discord" | "whatsapp" | "teams" | "google_chat" | "signal";

export interface Channel {
  id: string;
  type: ChannelType;
  name: string;
  agentId: string;
  config: string; // "[encrypted]" in listing
  enabled: boolean;
  createdAt: string;
}

export const listChannels = () =>
  request<{ channels: Channel[] }>("GET", "/channels");
export const getChannel = (id: string) =>
  request<Channel>("GET", `/channels/${id}`);
export const createChannel = (data: {
  type: ChannelType; name: string; agentId: string; config: Record<string, unknown>; enabled?: boolean;
}) => request<Channel>("POST", "/channels", data);
export const updateChannel = (id: string, data: { name?: string; config?: Record<string, unknown>; enabled?: boolean }) =>
  request<Channel>("PUT", `/channels/${id}`, data);
export const deleteChannel = (id: string) =>
  request<{ deleted: boolean }>("DELETE", `/channels/${id}`);
export const testChannel = (id: string) =>
  request<{ success: boolean; type: string; message: string }>("POST", `/channels/${id}/test`);

// Audit
export interface AuditEntry {
  id: string;
  timestamp: string;
  level: "info" | "warn" | "error" | "critical";
  category: string;
  action: string;
  agentId?: string;
  userId?: string;
  details?: string;
}
export const listAuditEntries = (limit = 200) =>
  request<{ entries: AuditEntry[] }>("GET", `/audit?limit=${limit}`);

// Activity Stream (real-time audit SSE)
export function streamActivity(
  onEvent: (entry: AuditEntry) => void,
  onError?: (error: Error) => void,
  agentId?: string,
): () => void {
  const params = new URLSearchParams();
  if (agentId) params.set("agentId", agentId);
  if (token) params.set("token", encodeURIComponent(token));
  const qs = params.toString();
  const url = `${BASE}/activity/stream${qs ? `?${qs}` : ""}`;
  const eventSource = new EventSource(url);

  eventSource.addEventListener("audit_entry", (e) => {
    try {
      onEvent(JSON.parse(e.data));
    } catch {
      // Skip unparseable data
    }
  });

  eventSource.onerror = () => {
    onError?.(new Error("Activity stream connection lost"));
    eventSource.close();
  };

  return () => eventSource.close();
}

// SSE Streaming
export function streamConversation(
  conversationId: string,
  onEvent: (event: StreamEvent) => void,
  onError?: (error: Error) => void,
): () => void {
  const url = `${BASE}/conversations/${conversationId}/stream`;
  const eventSource = new EventSource(
    token ? `${url}?token=${encodeURIComponent(token)}` : url,
  );

  eventSource.addEventListener("text", (e) => {
    onEvent({ type: "text", data: JSON.parse(e.data) });
  });

  eventSource.addEventListener("tool_call_start", (e) => {
    onEvent({ type: "tool_call_start", data: JSON.parse(e.data) });
  });

  eventSource.addEventListener("tool_call_result", (e) => {
    onEvent({ type: "tool_call_result", data: JSON.parse(e.data) });
  });

  eventSource.addEventListener("approval_required", (e) => {
    onEvent({ type: "approval_required", data: JSON.parse(e.data) });
  });

  eventSource.addEventListener("done", (e) => {
    onEvent({ type: "done", data: JSON.parse(e.data) });
    eventSource.close();
  });

  eventSource.onerror = () => {
    onError?.(new Error("SSE connection lost"));
    eventSource.close();
  };

  return () => eventSource.close();
}

// ── Types ───────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  readonly status: number;
  readonly data?: unknown;
  constructor(status: number, message: string, data?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export interface UserSummary {
  id: string;
  email: string;
  displayName: string;
  roles: string[];
}

export interface Agent {
  id: string;
  name: string;
  description?: string;
  systemPrompt: string;
  model: string;
  provider?: string | null;
  apiBaseUrl?: string | null;
  toolPolicyProfile: string;
  enabled: boolean;
  createdAt: string;
}

export interface CreateAgentInput {
  name: string;
  description?: string;
  systemPrompt: string;
  model?: string;
  provider?: string;
  apiBaseUrl?: string;
  toolPolicyProfile?: string;
}

export interface Conversation {
  id: string;
  agentId: string;
  userId: string;
  state: string;
  createdAt: string;
}

export interface ConversationSummary {
  id: string;
  agentId: string;
  state: string;
  title: string | null;
  totalInputTokens: number;
  totalOutputTokens: number;
  createdAt: string;
  updatedAt: string;
  preview: string | null;
}

export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
}

export interface ConversationUsage {
  conversationId: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

export interface Message {
  role: "user" | "assistant" | "system" | "tool";
  content: string;
  toolCallId?: string;
  toolName?: string;
}

export interface Tool {
  id: string;
  name: string;
  description: string;
  riskLevel: string;
  enabled: boolean;
}

export interface Workflow {
  id: string;
  name: string;
  description?: string;
  definition: { nodes: WorkflowNode[]; edges: WorkflowEdge[] };
  enabled: boolean;
  createdAt: string;
}

export interface WorkflowNode {
  id: string;
  type: string;
  label?: string;
  config: Record<string, unknown>;
  position?: { x: number; y: number };
  retryPolicy?: { maxAttempts: number; strategy: string; delayMs: number };
}

export interface WorkflowEdge {
  from: string;
  to: string;
  condition?: string;
}

export interface CreateWorkflowInput {
  name: string;
  description?: string;
  definition: { nodes: WorkflowNode[]; edges: WorkflowEdge[] };
  agentId: string;
}

export interface UpdateWorkflowInput {
  name?: string;
  description?: string;
  definition?: { nodes: WorkflowNode[]; edges: WorkflowEdge[] };
  enabled?: boolean;
}

export interface WorkflowRun {
  runId: string;
  workflowId: string;
  state: string;
  input: Record<string, unknown>;
  output?: Record<string, unknown>;
  nodeStates?: Record<string, { state: string; output?: unknown; error?: string; durationMs?: number }>;
  debugState?: DebugState;
  createdAt: string;
}

export interface DebugState {
  debugMode: boolean;
  currentDepth: number;
  totalDepths: number;
  stepIndex: number;
  nextNodeIds: string[];
  completedNodeIds: string[];
  skippedNodeIds: string[];
  currentNodeId?: string;
  executionPath: string[];
}

export interface WorkflowRunState {
  run: WorkflowRun;
  debugState: DebugState | null;
  nodeRuns: Array<{ nodeId: string; state: string; output?: unknown; error?: string; durationMs?: number }>;
}

export type SkillTier = "canonical" | "community" | "runtime";

export interface Skill {
  id: string;
  skillId: string;
  name: string;
  version: string;
  author?: string;
  tools: string[];
  riskLevel: string;
  approved: boolean;
  createdAt: string;
  tier: SkillTier;
  category?: string;
  tags?: string[];
  installCount: number;
  authorVerified: boolean;
}

export type StreamEvent =
  | { type: "text"; data: { token: string } }
  | { type: "tool_call_start"; data: { toolCallId: string; toolName: string; args: Record<string, unknown> } }
  | { type: "tool_call_result"; data: { toolCallId: string; status: string; output: unknown; durationMs: number } }
  | { type: "approval_required"; data: { toolCallId: string; toolName: string; prompt: string } }
  | { type: "error"; data: { error: string } }
  | { type: "done"; data: { finishReason: string } };
